/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable} from '@angular/core';
import {BaseService} from './base.service';
import {CompoApiService} from './compo-api.service';
import {Observable, Subject} from 'rxjs';
import {ApiResponse} from '../interfaces/ApiResponse';
import {catchError} from 'rxjs/operators';
import {HttpParams} from '@angular/common/http';
import {Offer} from '../interfaces/Offer';
import {PageData} from '../interfaces/PageData';
import {OfferProduct} from '../interfaces/OfferProduct';

@Injectable({
  providedIn: 'root'
})
export class OfferService extends BaseService {

  private deleteOfferSubject$ = new Subject<Offer>();
  private createOfferSubject$ = new Subject<Offer>();
  private archiveOfferSubject$ = new Subject<Offer>();
  private restoreOfferSubject$ = new Subject<Offer>();
  private updateOfferPriceSubject$ = new Subject<OfferProduct>();

  constructor(private readonly apiService: CompoApiService) {
    super();
  }

  getDeleteOfferSubject(): Observable<Offer> {
    return this.deleteOfferSubject$.asObservable();
  }

  nextDeleteOfferSubject(offer: Offer) {
    this.deleteOfferSubject$.next(offer);
  }

  getCreateOfferSubject(): Observable<Offer> {
    return this.createOfferSubject$.asObservable();
  }

  nextCreateOfferSubject(offer: Offer) {
    this.createOfferSubject$.next(offer);
  }

  getArchiveOfferSubject(): Observable<Offer> {
    return this.archiveOfferSubject$.asObservable();
  }

  getRestoreOfferSubject(): Observable<Offer> {
    return this.restoreOfferSubject$.asObservable();
  }

  getUpdateOfferPriceSubject(): Observable<OfferProduct> {
    return this.updateOfferPriceSubject$.asObservable();
  }

  nextUpdateOfferPriceSubject(offerProduct: OfferProduct) {
    this.updateOfferPriceSubject$.next(offerProduct);
  }

  get(id: number): Observable<ApiResponse<Offer>> {
    return this.apiService.get<Offer>('/offer-template/' + id).pipe(catchError(err => this.handleError(err)));
  }

  getProducts(id: number): Observable<ApiResponse<OfferProduct[]>> {
    return this.apiService.get<OfferProduct[]>('/offer-template/' + id + '/products').pipe(catchError(err => this.handleError(err)));
  }

  create(model: Offer): Observable<ApiResponse<Offer>> {
    return this.apiService.post<Offer, Offer>('/offer-template', model).pipe(catchError(err => this.handleError(err)));
  }

  update(model: Offer): Observable<ApiResponse<Offer>> {
    return this.apiService.patch<Offer, Offer>('/offer-template/' + model.id, model).pipe(catchError(err => this.handleError(err)));
  }

  put(model: Offer): Observable<ApiResponse<Offer>> {
    return this.apiService.putForm<Offer, Offer>('/offer-template', model).pipe(catchError(err => this.handleError(err)));
  }

  filter(params: HttpParams): Observable<ApiResponse<PageData<Offer[]>>> {
    return this.apiService.getWithParams<PageData<Offer[]>>('/offer-template', params).pipe(catchError(err => this.handleError(err)));
  }

  delete(offer: Offer): Observable<any> {
    return this.apiService.remove('/offer-template/' + offer.id).pipe(catchError(err => this.handleError(err)));
  }

  deleteArchive(): Observable<any> {
    return this.apiService.remove('/offer-template/delete-archive').pipe(catchError(err => this.handleError(err)));
  }

  repairArchive(): Observable<any> {
    return this.apiService.get<any>('/offer-template/repair-archive').pipe(catchError(err => this.handleError(err)));
  }

  // markup(offer: Offer, data: ExtraCharge): Observable<any> {
  //   return this.apiService.patch<ExtraCharge, any>('/offer-template/' + offer.id + '/markup', data).
  //   pipe(catchError(err => this.handleError(err)));
  // }

  updatePrices(offer: Offer, data: Map<number, number>): Observable<any> {
    const convMap = {};
    data.forEach((val: number, key: number) => {
      convMap[key] = val;
    });
    return this.apiService.patch<any, any>('/offer-template/' + offer.id + '/update-prices', convMap).pipe(catchError(err => this.handleError(err)));
  }

  //
  // emailOffer(id: number, data: Email): Observable<any> {
  //   return this.apiService.post<Email, any>('/offer-template/' + id + '/email', data).pipe(catchError(err => this.handleError(err)));
  // }

  export(id: number): Observable<Blob> {
    return this.apiService.downloadUrl('/offer-template/' + id + '/export').pipe(catchError(err => this.handleError(err)));
  }
}
